import type { Board, Difficulty } from './types';
import { CELLS, SIZE, boxes, cols, isValid, peers, rows, units } from './board';

export interface Elimination {
  index: number;
  digit: number;
}

export interface Step {
  technique: string;
  cells: number[];
  candidates: number[];
  placement: { index: number; value: number } | null;
  eliminations: Elimination[];
  unit: number[] | null;
  unitKind: 'row' | 'column' | 'box' | null;
}

export const TECHNIQUE_RANK: Record<string, number> = {
  'Naked single': 0,
  'Hidden single': 0,
  'Naked pair': 1,
  'Hidden pair': 1,
  'Pointing pair/triple': 2,
  'Box/line reduction': 2,
};

const DIFFICULTY_BY_RANK: Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

/** Candidate grid: Set of legal digits per empty cell, empty for filled cells. */
export type CandidateGrid = Set<number>[];

export function buildCandidates(board: Board): CandidateGrid {
  return board.map((value, i) => {
    if (value !== 0) return new Set<number>();
    const used = new Set<number>();
    for (const p of peers[i]!) {
      const v = board[p]!;
      if (v !== 0) used.add(v);
    }
    const set = new Set<number>();
    for (let d = 1; d <= SIZE; d++) {
      if (!used.has(d)) set.add(d);
    }
    return set;
  });
}

function applyStep(board: Board, cand: CandidateGrid, step: Step): void {
  if (step.placement) {
    const { index, value } = step.placement;
    board[index] = value;
    cand[index] = new Set<number>();
    for (const p of peers[index]!) cand[p]!.delete(value);
  }
  for (const { index, digit } of step.eliminations) {
    cand[index]!.delete(digit);
  }
}

// ---------------------------------------------------------------------------
// Technique finders. Each returns the first applicable Step, or null.
// ---------------------------------------------------------------------------

function findNakedSingle(board: Board, cand: CandidateGrid): Step | null {
  for (let i = 0; i < CELLS; i++) {
    if (board[i] !== 0) continue;
    if (cand[i]!.size === 1) {
      const value = [...cand[i]!][0]!;
      return {
        technique: 'Naked single',
        cells: [i],
        candidates: [value],
        placement: { index: i, value },
        eliminations: [],
        unit: null,
        unitKind: null,
      };
    }
  }
  return null;
}

function unitKindOf(unitIndex: number): 'row' | 'column' | 'box' {
  if (unitIndex < 9) return 'row';
  if (unitIndex < 18) return 'column';
  return 'box';
}

function findHiddenSingle(board: Board, cand: CandidateGrid): Step | null {
  for (let u = 0; u < units.length; u++) {
    const unit = units[u]!;
    for (let d = 1; d <= SIZE; d++) {
      let count = 0;
      let where = -1;
      for (const i of unit) {
        if (board[i] === 0 && cand[i]!.has(d)) {
          count++;
          where = i;
          if (count > 1) break;
        }
      }
      if (count === 1 && cand[where]!.size > 1) {
        return {
          technique: 'Hidden single',
          cells: [where],
          candidates: [d],
          placement: { index: where, value: d },
          eliminations: [],
          unit,
          unitKind: unitKindOf(u),
        };
      }
    }
  }
  return null;
}

function findNakedPair(board: Board, cand: CandidateGrid): Step | null {
  for (let u = 0; u < units.length; u++) {
    const unit = units[u]!;
    const pairCells = unit.filter((i) => board[i] === 0 && cand[i]!.size === 2);
    for (let a = 0; a < pairCells.length; a++) {
      for (let b = a + 1; b < pairCells.length; b++) {
        const ca = pairCells[a]!;
        const cb = pairCells[b]!;
        const da = [...cand[ca]!];
        if (da[0] !== [...cand[cb]!][0] || da[1] !== [...cand[cb]!][1]) continue;
        const [d1, d2] = da as [number, number];
        const eliminations: Elimination[] = [];
        for (const i of unit) {
          if (i === ca || i === cb || board[i] !== 0) continue;
          if (cand[i]!.has(d1)) eliminations.push({ index: i, digit: d1 });
          if (cand[i]!.has(d2)) eliminations.push({ index: i, digit: d2 });
        }
        if (eliminations.length > 0) {
          return {
            technique: 'Naked pair',
            cells: [ca, cb],
            candidates: [d1, d2],
            placement: null,
            eliminations,
            unit,
            unitKind: unitKindOf(u),
          };
        }
      }
    }
  }
  return null;
}

function findHiddenPair(board: Board, cand: CandidateGrid): Step | null {
  for (let u = 0; u < units.length; u++) {
    const unit = units[u]!;
    const positions = new Map<number, number[]>();
    for (let d = 1; d <= SIZE; d++) {
      const cells = unit.filter((i) => board[i] === 0 && cand[i]!.has(d));
      if (cells.length === 2) positions.set(d, cells);
    }
    const digits = [...positions.keys()];
    for (let a = 0; a < digits.length; a++) {
      for (let b = a + 1; b < digits.length; b++) {
        const d1 = digits[a]!;
        const d2 = digits[b]!;
        const p1 = positions.get(d1)!;
        const p2 = positions.get(d2)!;
        if (p1[0] !== p2[0] || p1[1] !== p2[1]) continue;
        const [ca, cb] = p1 as [number, number];
        const eliminations: Elimination[] = [];
        for (const cell of [ca, cb]) {
          for (const d of cand[cell]!) {
            if (d !== d1 && d !== d2) eliminations.push({ index: cell, digit: d });
          }
        }
        if (eliminations.length > 0) {
          return {
            technique: 'Hidden pair',
            cells: [ca, cb],
            candidates: [d1, d2],
            placement: null,
            eliminations,
            unit,
            unitKind: unitKindOf(u),
          };
        }
      }
    }
  }
  return null;
}

/** Pointing: within a box, a digit confined to one row/col eliminates it from the rest of that line. */
function findPointing(board: Board, cand: CandidateGrid): Step | null {
  for (let bx = 0; bx < SIZE; bx++) {
    const box = boxes[bx]!;
    for (let d = 1; d <= SIZE; d++) {
      const cells = box.filter((i) => board[i] === 0 && cand[i]!.has(d));
      if (cells.length < 2) continue;
      const sameRow = cells.every((i) => Math.floor(i / SIZE) === Math.floor(cells[0]! / SIZE));
      const sameCol = cells.every((i) => i % SIZE === cells[0]! % SIZE);
      let line: number[] | null = null;
      if (sameRow) {
        line = rows[Math.floor(cells[0]! / SIZE)]!;
      } else if (sameCol) {
        line = cols[cells[0]! % SIZE]!;
      }
      if (!line) continue;
      const eliminations: Elimination[] = [];
      for (const i of line) {
        if (box.includes(i) || board[i] !== 0) continue;
        if (cand[i]!.has(d)) eliminations.push({ index: i, digit: d });
      }
      if (eliminations.length > 0) {
        return {
          technique: 'Pointing pair/triple',
          cells,
          candidates: [d],
          placement: null,
          eliminations,
          unit: box,
          unitKind: 'box',
        };
      }
    }
  }
  return null;
}

/** Box/line reduction: within a line, a digit confined to one box eliminates it from the rest of that box. */
function findBoxLineReduction(board: Board, cand: CandidateGrid): Step | null {
  const lines: { cells: number[]; kind: 'row' | 'column' }[] = [
    ...rows.map((cells) => ({ cells, kind: 'row' as const })),
    ...cols.map((cells) => ({ cells, kind: 'column' as const })),
  ];
  for (const { cells: line, kind } of lines) {
    for (let d = 1; d <= SIZE; d++) {
      const cellsWithD = line.filter((i) => board[i] === 0 && cand[i]!.has(d));
      if (cellsWithD.length < 2) continue;
      const boxIndex = Math.floor(Math.floor(cellsWithD[0]! / SIZE) / 3) * 3 +
        Math.floor((cellsWithD[0]! % SIZE) / 3);
      const sameBox = cellsWithD.every((i) => {
        const bi = Math.floor(Math.floor(i / SIZE) / 3) * 3 + Math.floor((i % SIZE) / 3);
        return bi === boxIndex;
      });
      if (!sameBox) continue;
      const box = boxes[boxIndex]!;
      const eliminations: Elimination[] = [];
      for (const i of box) {
        if (line.includes(i) || board[i] !== 0) continue;
        if (cand[i]!.has(d)) eliminations.push({ index: i, digit: d });
      }
      if (eliminations.length > 0) {
        return {
          technique: 'Box/line reduction',
          cells: cellsWithD,
          candidates: [d],
          placement: null,
          eliminations,
          unit: line,
          unitKind: kind,
        };
      }
    }
  }
  return null;
}

/** Finders in order of increasing difficulty; hints and rating share this order. */
export const FINDERS: ((board: Board, cand: CandidateGrid) => Step | null)[] = [
  findNakedSingle,
  findHiddenSingle,
  findNakedPair,
  findHiddenPair,
  findPointing,
  findBoxLineReduction,
];

/** Find the simplest applicable logical step for the given board, or null. */
export function findStep(board: Board, cand: CandidateGrid): Step | null {
  for (const finder of FINDERS) {
    const step = finder(board, cand);
    if (step) return step;
  }
  return null;
}

interface LogicalResult {
  board: Board;
  techniques: string[];
  solved: boolean;
}

/** Solve as far as pure logic (the implemented techniques) allows. */
export function solveLogically(input: Board): LogicalResult {
  const board = [...input];
  const cand = buildCandidates(board);
  const techniques = new Set<string>();
  for (;;) {
    const step = findStep(board, cand);
    if (!step) break;
    techniques.add(step.technique);
    applyStep(board, cand, step);
  }
  const solved = board.every((v) => v !== 0);
  return { board, techniques: [...techniques], solved };
}

// ---------------------------------------------------------------------------
// Backtracking solver for uniqueness.
// ---------------------------------------------------------------------------

function search(board: Board, limit: number, out: { count: number; solution: Board | null }): void {
  if (out.count >= limit) return;
  // Pick the empty cell with the fewest candidates (MRV).
  let best = -1;
  let bestCands: number[] | null = null;
  for (let i = 0; i < CELLS; i++) {
    if (board[i] !== 0) continue;
    const used = new Set<number>();
    for (const p of peers[i]!) {
      const v = board[p]!;
      if (v !== 0) used.add(v);
    }
    const options: number[] = [];
    for (let d = 1; d <= SIZE; d++) if (!used.has(d)) options.push(d);
    if (options.length === 0) return; // dead end
    if (bestCands === null || options.length < bestCands.length) {
      best = i;
      bestCands = options;
      if (options.length === 1) break;
    }
  }
  if (best === -1) {
    // No empty cells: a complete solution.
    out.count++;
    if (out.solution === null) out.solution = [...board];
    return;
  }
  for (const d of bestCands!) {
    board[best] = d;
    search(board, limit, out);
    board[best] = 0;
    if (out.count >= limit) return;
  }
}

/** Returns the unique solution, or null if there are zero or multiple solutions. */
export function solve(board: Board): Board | null {
  if (!isValid(board)) return null;
  const out = { count: 0, solution: null as Board | null };
  search([...board], 2, out);
  return out.count === 1 ? out.solution : null;
}

/** True if the board has exactly one solution. */
export function hasUniqueSolution(board: Board): boolean {
  return solve(board) !== null;
}

/**
 * Rate a puzzle by the hardest technique required.
 * Returns null if the puzzle is unsolvable or non-unique.
 */
export function rate(givens: Board): { difficulty: Difficulty; techniques: string[] } | null {
  if (!isValid(givens)) return null;
  if (!hasUniqueSolution(givens)) return null;
  const { techniques, solved } = solveLogically(givens);
  let rank = 0;
  for (const t of techniques) {
    rank = Math.max(rank, TECHNIQUE_RANK[t] ?? 0);
  }
  if (!solved) rank = 3; // requires search beyond the implemented logic
  return { difficulty: DIFFICULTY_BY_RANK[rank]!, techniques };
}
